import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import signupImage from "../noevent/signupImage.png";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Import icons
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../store/useAuth";
import { getPostLoginRedirect } from "../utils/authSession";
import { login, googleSignup } from "../services/auth";

const EventLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const { setLoginSession, fetchMe } = useAuth();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleRedirect = () => {
    navigate("/signup");
  };
  const handleRedirectToForgot = () => {
    navigate("/forgot-password");
  };
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { email, password } = formData;

    // Validation checks
    if (!email || !password) {
      toast.error("Please fill in all required fields", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    if (!emailRegex.test(email)) {
      toast.error("Invalid email format. Please enter a valid email.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const trimmedEmail = email.trim();
    setIsLoading(true);
    const redirectTo = getPostLoginRedirect() || "/dashboard";

    try {
      const res = await login({ email: trimmedEmail, password });

      const data = res.data;
      if (data.message === "failure") {
        throw new Error(
          data.errorMessage || "Invalid credentials. Please try again.",
        );
      }

      toast.success("Logged in successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      setLoginSession({
        userId: data?.data?.userId || data?.data?.user?.id || data?.user?.id,
        sessionId: data?.data?.sessionId || data?.sessionId,
      });
      await fetchMe(true);

      navigate(redirectTo, { replace: true }); // Redirect after successful login
    } catch (error) {
      const message = error.response?.data?.errorMessage || error.message;
      const isInactive = message?.includes("Account is inactive");
      toast.error(
        isInactive
          ? "Account is not active. Please contact admin to activate account."
          : message || "Something went wrong",
        {
          position: "top-right",
          autoClose: 3000,
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const redirectTo = getPostLoginRedirect() || "/dashboard";
      const userData = {
        name: decoded.name,
        email: decoded.email,
        picture: decoded.picture,
        googleId: decoded.sub,
      };

      const res = await googleSignup(userData);

      const data = res.data;

      toast.success("Logged in successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      setLoginSession({
        userId: data?.data?.userId || data?.data?.user?.id || data?.user?.id,
        sessionId: data?.data?.sessionId || data?.sessionId,
      });
      await fetchMe(true);
      navigate(redirectTo, { replace: true }); // Redirect after successful login
    } catch (error) {
      const message = error.response?.data?.errorMessage || error?.message;
      toast.error(
        message?.includes("Account is inactive")
          ? "Account is not active. Please contact admin to activate account."
          : message || "Google signup failed",
      );
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <ToastContainer />
      {/* Left Section */}
      <div className="w-full md:w-1/2 bg-[#1E374D] flex items-center justify-center p-6 md:p-8">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl text-white font-bold mb-4">
            Event Management
          </h2>
          <p className="text-sm md:text-base text-gray-200">
            Manage your events seamlessly with our platform.
          </p>
          <img
            src={signupImage}
            alt="login"
            className="w-full max-w-xs mx-auto"
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="w-full max-w-md bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-center mb-6">Login</h2>

          {/* Social Login */}
          <div className="flex flex-col items-center justify-center gap-4 mb-4">
            <GoogleLogin
              onSuccess={handleGoogleSignUp}
              onError={() => toast.error("Google Sign-In failed")}
              width="100%"
            />
          </div>

          <div className="text-center my-4 text-gray-500 text-sm sm:text-base">
            - OR -
          </div>

          {/* Form Inputs */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm sm:text-base mb-1"
                htmlFor="email"
              >
                Email
              </label>
              <input
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm sm:text-base mb-1"
                htmlFor="password"
              >
                Password
              </label>

              {/* Input Field with Eye Icon */}
              <div className="relative w-full">
                <input
                  type={showPassword ? "text" : "password"} // Toggle type
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                />

                {/* Eye Icon Button */}
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 text-sm sm:text-base rounded-md hover:bg-blue-700"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </form>

          {/* Sign Up Redirect */}
          <p className="text-center text-sm text-gray-600 mt-4">
            Don't have an account?{" "}
            <span
              onClick={handleRedirect}
              className="text-blue-600 hover:underline cursor-pointer"
            >
              Sign Up
            </span>
          </p>

          <p className="text-center text-sm text-gray-600 mt-4">
            <span
              onClick={handleRedirectToForgot}
              className="text-blue-600 hover:underline cursor-pointer"
            >
              Forgot Password?{" "}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventLogin;
